<template>
  <div class="upload-button-item" v-loading="loading">
    <div v-if="isOnline">
      <label id="upload-photo-button" :for="inputId">
        <slot name="icon"><i v-if="icon" :class="icon"></i></slot> {{ label }}
        <input
          @change="selectedFile"
          type="file"
          name="upload-photo"
          :id="inputId"
          :capture="capture"
          style="display: none"
          accept="image/*"
        />
      </label>
      <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
    </div>
    <div v-else class="error-message">
      現在オフラインのためアップロードはご利用頂けません。ネットワークに繋げてからお試しください。
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, getCurrentInstance } from 'vue'
import { useOnline } from '@vueuse/core'
import { Message } from 'element-ui'
import { uploadImage } from '@/services/image-upload-client'

withDefaults(
  defineProps<{
    label?: string
    icon?: string
    capture?: string | null
  }>(),
  {
    label: '写真をアップロードする',
    icon: 'el-icon-picture',
    capture: null
  }
)

const emit = defineEmits<{
  (e: 'upload-photo-complete', imageSrc: string): void
}>()

// GTM だけはモジュール入口が無いため instance 経由で取る（CLAUDE.md: 依存を増やさない範囲で最小限）
const gtm = (
  getCurrentInstance()?.proxy as unknown as {
    $gtm?: { sendError: (area: string, message: string) => void }
  }
)?.$gtm

const isOnline = useOnline()
const loading = ref(false)
const errorMessage = ref('')
const inputId = `upload-photo-${Math.random().toString(36).substring(2, 11)}`

async function selectedFile(event: Event) {
  event.preventDefault()
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  // 同じファイルを選び直しても change が発火するように値を戻す
  // （File は取得済みなのでここでクリアしてよい）
  input.value = ''
  if (!file) return

  if (!isOnline.value) {
    Message({
      message:
        '現在オフラインです。インターネットにつながってから再度お試しください',
      type: 'warning'
    })
    return
  }

  loading.value = true
  try {
    const imageSrc = await uploadImage(file)
    errorMessage.value = ''
    // 同じ写真を選び直すと応答の data URL が前回と同一になる。値の変化を条件に
    // すると発火しないため、アップロード成功のたびにそのまま emit する
    emit('upload-photo-complete', imageSrc)
  } catch (error) {
    console.error(error)
    gtm?.sendError('upload photo', String(error))
    errorMessage.value = 'アップロードに失敗しました'
  } finally {
    loading.value = false
  }
}
</script>
